import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Outlet } from "react-router-dom";
import { Navbar, Nav, Spinner, Alert } from "react-bootstrap";
import {
  FaPhone, FaUserCircle, FaSignOutAlt, FaChartLine, FaUserShield,
  FaMapMarkedAlt, FaUserTie, FaUsers, FaFileSignature, FaClipboardList,
  FaTruck, FaFileInvoiceDollar, FaChartPie, FaBoxOpen, FaCogs, FaBone,
  FaScrewdriver, FaCube, FaFileContract,
} from "react-icons/fa";
import { clearAuth, isAuthenticated, canAccessMenu, hasAction } from "./utils/auth";
import sessionStorage from "./utils/sessionStore";
import { buildSlugPath, getLeafSlug } from "./utils/menuSlug";
import "./pagecss/Webappcss.css";

const API_URL = process.env.REACT_APP_API_URL;

// Map of menuIds that should open a PDF in a new tab
const pdfMenuMap = {
  9: '/Documents/SHUKLA-Systems-All-in-One-Brochure-2026.pdf',
  11: '/Documents/SpecialOrderPartsBrochure2025.pdf'
};

// Drawer block icons — resolved by menu TITLE (no DB column needed; the Menu
// table carries no icon info). Unmapped items fall back to a cube. If icons
// ever need to be admin-configurable, add an IconKey column to Menu instead.
const PANEL_ICONS = {
  "sales territory": FaMapMarkedAlt,
  "sales representatives": FaUserTie,
  "customers": FaUsers,
  // A signed sheet of paper, deliberately distinct from Quotes' pen-on-document: a price
  // contract is the thing that was AGREED, not the thing being offered.
  "price contracts": FaFileContract,
  "quotes": FaFileSignature,
  "orders": FaClipboardList,
  "shipping": FaTruck,
  "invoices": FaFileInvoiceDollar,
  "finance": FaChartPie,
  "full product suite": FaBoxOpen,
  "special order parts": FaCogs,
  "shukla hip": FaBone,
  "shukla knee": FaBone,
  "shukla upper": FaBone,
  "shukla nail": FaBone,
  "shukla spine": FaBone,
  "shukla screw": FaScrewdriver,
};
const panelIconFor = (item) =>
  PANEL_ICONS[(item.menuTitle || "").trim().toLowerCase()] || FaCube;

// One-time cleanup: remove old localStorage tokens left from before sessionStorage migration
if (typeof window !== 'undefined' && window.localStorage) {
  ['authToken', 'userEmail', 'userName'].forEach(k => localStorage.removeItem(k));
}

function NavbarLayout() {
  const navigate = useNavigate();
  const location = useLocation();

  const [menuData, setMenuData] = useState([]);
  const [isActive, setIsActive] = useState(false);
  const [selectedPath, setSelectedPath] = useState([]);
  // Desktop floating drawer: the side menu overlays the (always full-width)
  // content; opened by the navbar menu icon or by clicking Products/Sales.
  const [panelOpen, setPanelOpen] = useState(false);
  const [parentZones, setParentZones] = useState({});
  const [mobileMenuLevel, setMobileMenuLevel] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const isMobile = () => window.innerWidth < 992;

  const menuRef = useRef(null);
  const navbarRef = useRef(null);

  // =========================
  // ZOOM-SYNC — the navbar AND every page shell (.content-halo) share one
  // "design canvas" (1400px landscape, 390px portrait) that is scaled with CSS
  // `zoom` to fill the viewport width. Because both scale by the same factor, the
  // whole UI looks identical at every browser-zoom level (fonts, spacing, the gap
  // below the navbar — everything scales as one unit) until the viewport flips to
  // the narrow/portrait canvas. The content shell flows with normal page scroll at
  // this scale (the old fixed-height internal-scroll model can't survive `zoom`).
  // =========================
  useEffect(() => {
    // Trial UI scale — the navbar and the page content each render at a fraction
    // of the original 1400px design canvas. LOWER = smaller (more fits); 1.0 =
    // original size. A larger design canvas means the same viewport holds "more"
    // canvas, so things draw smaller while still filling the width. The two can
    // differ (e.g. a roomier menu over a denser grid). Tune these to taste.
    const NAV_UI_SCALE = 0.8;       // navbar
    const CONTENT_UI_SCALE = 0.6;   // grid / page content
    const BASE_CANVAS = 1400;
    const NAV_LANDSCAPE = Math.round(BASE_CANVAS / NAV_UI_SCALE);         // 0.8 → 1750
    const CONTENT_LANDSCAPE = Math.round(BASE_CANVAS / CONTENT_UI_SCALE); // 0.6 → ~2333
    const PORTRAIT_WIDTH = 390;     // mobile: one canvas for both

    const applyCanvasZoom = () => {
      const vw = document.documentElement.clientWidth || window.innerWidth;
      const vh = document.documentElement.clientHeight || window.innerHeight;
      const isLandscape = vw >= vh;
      const navCanvas = isLandscape ? NAV_LANDSCAPE : PORTRAIT_WIDTH;
      const contentCanvas = isLandscape ? CONTENT_LANDSCAPE : PORTRAIT_WIDTH;
      const navScale = vw / navCanvas;

      const nav = document.querySelector('.floating-nav-wrapper');
      if (nav) nav.style.zoom = navScale;

      // The navbar now scales independently of the content, so clear it by its
      // ACTUAL rendered height + a small gap. Convert to content-canvas px (each
      // element is zoomed by its own elScale, computed below).
      const navBottom = nav ? nav.getBoundingClientRect().bottom : 50 * navScale;
      // Gaps in CONTENT-CANVAS px so they scale with the grid — the gap stays
      // visually constant RELATIVE to the content at every browser-zoom level.
      // (A fixed-CSS-px gap grows against the vw-scaled content as you zoom in,
      // which is why the top/bottom gaps used to balloon on zoom.) The navbar
      // clearance is converted to canvas px (navBottom / elScale, per element)
      // so the content still sits just below the independently-scaled navbar.
      const GAP_TOP = 30;     // gap below the navbar (canvas px — raise for more)
      const GAP_BOTTOM = 30;  // gap above the screen bottom (canvas px)
      document.querySelectorAll('.content-halo').forEach((el) => {
        // Most content draws on the shared content canvas. Add/edit forms tagged
        // .dyn-form-2x use a SMALLER canvas (÷ FORM_ZOOM), so the same viewport
        // holds "less" — everything (fonts, fields, spacing) renders FORM_ZOOM×
        // larger while the container still fills ~96vw.
        const FORM_ZOOM = 1.5;  // add/edit form magnification vs. the grids
        const big = el.classList.contains('dyn-form-2x');
        const elCanvas = big ? Math.round(contentCanvas / FORM_ZOOM) : contentCanvas;
        const elScale = vw / elCanvas;
        const elMarginTop = Math.round(navBottom / elScale + GAP_TOP);
        const elMarginBottom = GAP_BOTTOM;

        el.style.zoom = elScale;
        // Fixed canvas width (96% of the canvas for side gutters); the zoom above
        // renders it at ~96vw.
        el.style.setProperty('width', Math.round(elCanvas * 0.96) + 'px', 'important');
        el.style.setProperty('max-width', 'none', 'important');

        const shellH = Math.max(240, Math.round(vh / elScale - elMarginTop - elMarginBottom));
        if (el.classList.contains('dyn-search-shell')) {
          // Grid pages: the shell fills the viewport HEIGHT and the results region
          // scrolls INSIDE it — the page itself never scrolls. Height is in canvas
          // px (vh / elScale) so it fills the viewport once scaled.
          el.style.setProperty('margin', elMarginTop + 'px auto ' + elMarginBottom + 'px', 'important');
          el.style.setProperty('height', shellH + 'px', 'important');
          el.style.setProperty('min-height', '0', 'important');
        } else if (el.classList.contains('dyn-form-shell')) {
          // Add/edit forms: the container HUGS the form when it's short, but is
          // CAPPED at the viewport height and scrolls the form body inside when it's
          // tall — so a short form isn't stranded in a giant empty box, and a long
          // form scrolls within the container rather than the whole page.
          el.style.setProperty('margin', elMarginTop + 'px auto ' + elMarginBottom + 'px', 'important');
          el.style.setProperty('max-height', shellH + 'px', 'important');
          el.style.setProperty('height', 'auto', 'important');
          el.style.setProperty('min-height', '0', 'important');
        } else {
          // Content pages (forms / review / wizard): natural height, page scroll.
          el.style.setProperty('margin', elMarginTop + 'px auto 40px', 'important');
          el.style.setProperty('height', 'auto', 'important');
          el.style.setProperty('min-height', '0', 'important');
        }
      });
    };

    // ── Canvas-zoom DISABLED ──────────────────────────────────────────────
    // The layout is now responsive via CSS (.content-halo / .dyn-*-shell rules),
    // so the UI sizes naturally and browser zoom (Ctrl +/-) works. We no longer
    // call applyCanvasZoom; instead we CLEAR any inline zoom/width/margin/height
    // overrides a previous canvas-zoom build may have left, so the CSS governs.
    void applyCanvasZoom; // keep the reference; intentionally not invoked
    const clearShellStyles = (el) => {
      if (!el) return;
      ['zoom', 'width', 'max-width', 'height', 'min-height', 'max-height', 'margin']
        .forEach((p) => el.style.removeProperty(p));
    };
    clearShellStyles(document.querySelector('.floating-nav-wrapper'));
    document.querySelectorAll('.content-halo').forEach(clearShellStyles);

    // ── Ultra-wide navbar zoom REMOVED ──────────────────────────────────
    // The pill used to zoom up beyond a 16:9 viewport ratio, which made the
    // menu fonts grow whenever the browser HEIGHT shrank, and broke the
    // 280px alignment with the (unzoomed) side menu pane. The navbar now
    // renders at fixed CSS px like the page content at every window size.
    // clearShellStyles above already strips any inline zoom a previous
    // build left on the wrapper.
  }, [location.pathname]);

  // =========================
  // LOAD MENU DATA
  // =========================
  useEffect(() => {
    const fetchMenuData = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const response = await fetch(`${API_URL}/api/Common/Menu`, { cache: 'no-store' });
        if (!response.ok) throw new Error(`Server error: ${response.status}`);

        const data = await response.json();
        setMenuData(data);
      } catch (err) {
        console.error('Error fetching menu:', err);
        if (err.message.includes('Failed to fetch')) {
          setError('Unable to connect to server. Please check if the API is running.');
        } else {
          setError(`Failed to load menu: ${err.message}`);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchMenuData();
  }, []);

  // Docked side menu: on logged-in APP pages (not the /home marketing tree,
  // which stays full-bleed) the desktop panel is permanently visible and the
  // content reflows beside it. panelOpen then only governs the logged-out
  // (Products) drawer, which keeps its float-and-dismiss behavior.
  const isMarketingRoute = location.pathname === "/home"
    || location.pathname.startsWith("/home/")
    || location.pathname === "/login";
  const isDockedMenu = isAuthenticated() && !isMarketingRoute;

  // =========================
  // CLICK OUTSIDE TO COLLAPSE
  // =========================
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Docked desktop panel never dismisses on outside clicks — it isn't a
      // drawer there. (Mobile still uses the collapse menu, handled below.)
      if (isDockedMenu && !isMobile()) return;
      // The desktop drawer FLOATS over the content, so clicking anywhere
      // outside it (or the navbar) closes it — same as the mobile menu.
      if (selectedPath.length === 0 && !isActive && !panelOpen) return;

      const clickedInsideNavbar = navbarRef.current && navbarRef.current.contains(event.target);
      const clickedInsideSubMenu = menuRef.current && menuRef.current.contains(event.target);

      if (!clickedInsideNavbar && !clickedInsideSubMenu) {
        setSelectedPath([]);
        setPanelOpen(false);
        setMobileMenuLevel(0);
        setIsActive(false);
      }
    };

    const handleWindowBlur = () => {
      if (isDockedMenu && !isMobile()) return;
      if (selectedPath.length > 0 || isActive || panelOpen) {
        setTimeout(() => {
          if (document.activeElement?.tagName === 'IFRAME') {
            setSelectedPath([]);
            setPanelOpen(false);
            setMobileMenuLevel(0);
            setIsActive(false);
          }
        }, 0);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    window.addEventListener("blur", handleWindowBlur);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      window.removeEventListener("blur", handleWindowBlur);
    };
  }, [selectedPath, isActive, panelOpen, isDockedMenu]);

  // =========================
  // LISTEN FOR NAVIGATION FROM IFRAME
  // =========================
  useEffect(() => {
    const handler = (event) => {
      if (!event.data) return;

      if (event.data.type === "NAVIGATE_MENU") {
        const menuIdFromHtml = event.data.menuId;
        if (!menuIdFromHtml) return;
        // Root home menu → bare /home; everything else → /home/<slug-or-id>.
        if (Number(menuIdFromHtml) === 1) {
          navigate('/home');
        } else {
          const slugPath = buildSlugPath(menuData, menuIdFromHtml);
          navigate(`/home/${slugPath || menuIdFromHtml}`);
        }
        setIsActive(false);
        setSelectedPath([]);
      }

      if (event.data.type === "NAVIGATE_PRODUCT_BOM") {
        const partNum = event.data.partNum;
        if (!partNum) return;
        navigate(`/product-bom/${partNum}`);
      }
    };

    window.addEventListener("message", handler);
    return () => window.removeEventListener("message", handler);
    // menuData is needed so buildSlugPath sees the current tree (otherwise
    // an early iframe message fires before menuData is loaded → numeric URL).
  }, [navigate, menuData]);

  // =========================
  // CALC ZONE
  // =========================
  const calcZone = (clickedElement) => {
    const wrapper = document.querySelector('.floating-nav-wrapper');
    if (!wrapper || !clickedElement) return { zone: 'left', leftOffset: 174 };
    const logo = wrapper.querySelector('img');
    const wrapperRect = wrapper.getBoundingClientRect();
    const logoRect = logo ? logo.getBoundingClientRect() : { right: wrapperRect.left + 174 };
    const menuStart = logoRect.right;
    const menuEnd = wrapperRect.right - 80;
    const menuWidth = menuEnd - menuStart;
    const thirdWidth = menuWidth / 3;
    const itemRect = clickedElement.getBoundingClientRect();
    const itemCenter = (itemRect.left + itemRect.right) / 2;
    const zoom = parseFloat(wrapper.style.zoom) || 1;
    const leftOffset = Math.max(174, (itemRect.left - wrapperRect.left) / zoom - 6);
    let zone = 'left';
    if (itemCenter >= menuStart + 2 * thirdWidth) zone = 'right';
    else if (itemCenter >= menuStart + thirdWidth) zone = 'center';
    return { zone, leftOffset };
  };

  // =========================
  // NAVIGATION
  // =========================
  const handleNavigate = (id, keepMenuOpen = false) => {
    const item = menuData.find(m => m.menuId === id);

    // PDF in new tab
    if (pdfMenuMap[Number(id)]) {
      window.open(pdfMenuMap[Number(id)], '_blank');
      if (!keepMenuOpen) setIsActive(false);
      return;
    }

    // Custom React route from DB (Menu.RoutePath)
    if (item && item.routePath) {
      navigate(item.routePath);
      if (!keepMenuOpen) setIsActive(false);
      return;
    }

    // Admin pages (search/create/edit) use a flat /:slug/{action} URL —
    // just the menu's own slug, no ancestor chain. /home/* keeps the full
    // hierarchical path so the marketing tree still produces readable URLs.
    const leafSlug = getLeafSlug(menuData, id);
    const adminSegment = leafSlug || String(id);

    if (item && item.hasSearchPage) {
      navigate(`/${adminSegment}/search`);
      if (!keepMenuOpen) setIsActive(false);
      return;
    }

    if (item && item.hasDynamicForm) {
      navigate(`/${adminSegment}/create`);
      if (!keepMenuOpen) setIsActive(false);
      return;
    }

    // Marketing pages — hierarchical path under /home so duplicate leaf
    // titles ("Blade" under both Hip and Knee) disambiguate via the parent.
    // Root home menu collapses to bare /home so the URL bar stays clean.
    const homeSlugPath = buildSlugPath(menuData, id);
    const homeSegment = homeSlugPath || String(id);
    navigate(Number(id) === 1 ? '/home' : `/home/${homeSegment}`);

    if (!keepMenuOpen) {
      setIsActive(false);
    }
  };

  // Whether a menu item navigates on its own (vs. a container that only opens a
  // submenu). Mirrors the onClick guard used in the renderers.
  const isNavigable = (item) =>
    !!item && (
      (item.hasContent !== false && item.hasContent !== 0) ||
      item.hasDynamicForm || item.hasSearchPage || item.routePath ||
      !!pdfMenuMap[Number(item.menuId)]
    );

  // The URL a menu item points at — SAME routing rules as handleNavigate, but
  // returned as a string so items can render as real <a href>. That's what
  // makes right-click "open in new tab", ctrl/⌘+click and middle-click work.
  // Returns undefined for container-only items (no own destination).
  const getMenuHref = (id) => {
    const item = menuData.find(m => m.menuId === id);
    if (!isNavigable(item)) return undefined;
    if (pdfMenuMap[Number(id)]) return pdfMenuMap[Number(id)];
    if (item.routePath) return item.routePath;
    const leafSlug = getLeafSlug(menuData, id);
    const adminSegment = leafSlug || String(id);
    if (item.hasSearchPage) return `/${adminSegment}/search`;
    if (item.hasDynamicForm) return `/${adminSegment}/create`;
    const homeSlugPath = buildSlugPath(menuData, id);
    const homeSegment = homeSlugPath || String(id);
    return Number(id) === 1 ? '/home' : `/home/${homeSegment}`;
  };

  // Plain left-click → in-app SPA navigation (prevent the browser following the
  // href). Modified clicks (ctrl/⌘/shift/alt) and middle-click fall through so
  // the browser opens the href in a new tab/window as the user asked.
  const isModifiedClick = (e) =>
    e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button === 1;

  const toggleMenu = () => {
    setIsActive(!isActive);
    setMobileMenuLevel(0);
  };

  // =========================
  // PATH HANDLING
  // =========================
  const handlePathUpdate = (level, id) => {
    const hasChildren = menuData.some(
      x => x.parentMenuId === id && x.isActive
    );

    const newPath = selectedPath.slice(0, level);
    newPath[level] = id;

    if (hasChildren) {
      setSelectedPath(newPath);
    } else {
      setSelectedPath([]);
      setIsActive(false);
    }
  };

  // =========================
  // AUTH-GATED MENUS (driven by Menu.RequiresAuth in DB)
  // =========================
  // Use isAuthenticated() (token present AND not past expiry) — not raw token
  // presence — so an expired-but-not-yet-cleared token doesn't show logged-in
  // chrome (protected menu items + dashboard/logout icons).
  const isLoggedIn = isAuthenticated();

  // =========================
  // ACTIVE TOP MENU (route-based highlight)
  // =========================
  // Which top-level menu owns the CURRENT page. Drives the persistent
  // highlight in the top bar: a leaf like Events navigates without opening a
  // submenu (selectedPath stays empty), and the Sales modules live on
  // bespoke routes — both still highlight their top-level owner.
  const topAncestorId = (menuId) => {
    let cur = menuData.find(m => m.menuId === menuId);
    while (cur && cur.parentMenuId !== 0) {
      const parentId = cur.parentMenuId;
      cur = menuData.find(m => m.menuId === parentId);
    }
    return cur ? cur.menuId : null;
  };

  const getActiveTopMenuId = () => {
    const segs = location.pathname.split('/').filter(Boolean);
    if (segs.length === 0) return null;
    if (segs[0] === 'home') {
      // Marketing pages: /home/<top-slug>/…
      if (!segs[1]) return null;
      const top = menuData.find(
        m => m.parentMenuId === 0 && m.isActive && m.slug === segs[1]
      );
      return top ? top.menuId : null;
    }
    // Bespoke module routes (/quote, /orders, …) match Menu.RoutePath;
    // dynamic admin pages (/customers-2/search, …) match the menu's slug.
    const item = menuData.find(
      m => m.isActive && m.routePath && m.routePath.split('/')[1] === segs[0]
    ) || menuData.find(
      m => m.isActive && m.slug === segs[0] && (m.hasSearchPage || m.hasDynamicForm)
    );
    return item ? topAncestorId(item.menuId) : null;
  };
  const activeTopMenuId = getActiveTopMenuId();

  // =========================
  // RENDER TOP MENU
  // =========================
  const renderTopMenu = () => {
    return menuData
      .filter(x => x.parentMenuId === 0 && x.isActive)
      .filter(x => !x.requiresAuth || isLoggedIn)
      .filter(x => !(x.hideWhenLoggedIn && isLoggedIn))
      // Role entitlements (Menu.RequiredActionId, delivered in the menu
      // payload): hide modules the role can't use…
      .filter(x => canAccessMenu(x))
      // …and hide an auth-only container (e.g. "Sales") when every one of
      // its children is entitlement-hidden — an empty dropdown is useless.
      .filter(x => {
        const children = menuData.filter(c => c.parentMenuId === x.menuId && c.isActive);
        if (children.length === 0) return true;
        const hasOwnContent = (x.hasContent !== false && x.hasContent !== 0)
          || x.hasSearchPage || x.hasDynamicForm || x.routePath;
        return hasOwnContent || children.some(c => canAccessMenu(c));
      })
      .map(item => {
        const hasChildren = menuData.some(
          x => x.parentMenuId === item.menuId && x.isActive && canAccessMenu(x)
        );
        const hasContent = item.hasContent !== false && item.hasContent !== 0;
        // Exactly ONE item highlights: an explicitly-opened submenu panel
        // wins (clicking Sales while on a Leadership page lights only
        // Sales); otherwise the current page's owner lights up (Events has
        // no submenu, so only the route can mark it; Sales stays lit while
        // on any of its modules).
        const isSelected = selectedPath.length > 0
          ? selectedPath[0] === item.menuId
          : activeTopMenuId === item.menuId;

        return (
          <Nav.Link
            key={item.menuId}
            className={isSelected ? "nav-link-selected" : ""}
            href={getMenuHref(item.menuId)}
            onClick={(e) => {
              // Modified/middle clicks fall through to the browser so the href
              // opens in a new tab/window.
              if (isModifiedClick(e)) return;
              e.preventDefault();
              if (isMobile()) {
                const zone = calcZone(e.currentTarget);
                setParentZones(prev => ({ ...prev, [item.menuId]: zone }));
                handlePathUpdate(0, item.menuId);
                if (hasChildren) {
                  setMobileMenuLevel(1);
                }
              } else if (hasChildren) {
                // Desktop: CLICK toggles the floating drawer with this
                // item's submenu (hover no longer opens submenus).
                if (panelOpen && selectedPath[0] === item.menuId) {
                  setPanelOpen(false);
                  setSelectedPath([]);
                } else {
                  setSelectedPath([item.menuId]);
                  setPanelOpen(true);
                }
              } else {
                // Desktop leaf (Events, About Us): navigating away closes the
                // drawer and drops its selection, so the old item doesn't
                // stay highlighted — route ownership takes over.
                setSelectedPath([]);
                setPanelOpen(false);
              }
              if (hasContent || item.hasDynamicForm || item.hasSearchPage || item.routePath || pdfMenuMap[Number(item.menuId)]) {
                handleNavigate(item.menuId, hasChildren);
              }
            }}
            style={{
              fontWeight: isSelected ? "700" : "bold",
              marginRight: "15px",
              borderRadius: "4px",
              padding: "4px 10px"
            }}
          >
            {item.menuTitle}
            {hasChildren && (
              <span style={{ marginLeft: "4px", fontSize: "10px" }}>&#9660;</span>
            )}
          </Nav.Link>
        );
      });
  };

  // =========================
  // RENDER SUB MENUS
  // =========================
  const renderSubMenus = () => {
    const levels = [];

    for (let level = 0; level <= selectedPath.length; level++) {
      const parentId = level > 0 ? selectedPath[level - 1] : 0;

      const items = menuData.filter(
        x =>
          x.parentMenuId === parentId &&
          x.isActive &&
          x.parentMenuId !== 0 &&
          canAccessMenu(x)   // role entitlements (Menu.RequiredActionId)
      );

      if (items.length > 0) {
        if (isMobile() && level !== mobileMenuLevel) {
          continue;
        }

        const parentInfo = parentZones[parentId] || { zone: 'left', leftOffset: 174 };
        const { zone, leftOffset } = parentInfo;
        let justifyContent = "flex-start";
        let padLeft = Math.max(174, leftOffset) + "px";
        if (zone === 'center') {
          justifyContent = "center";
          padLeft = "174px";
        } else if (zone === 'right') {
          justifyContent = "flex-end";
          padLeft = "174px";
        }

        levels.push(
          <div
            key={level}
            className="sublevel-row"
            style={{
              display: "flex",
              flexDirection: "row",
              flexWrap: "nowrap",
              alignItems: "center",
              width: "100%",
              height: "36px",
              justifyContent: justifyContent,
              paddingLeft: padLeft,
              paddingRight: 15,
              paddingTop: 0,
              paddingBottom: 0,
              overflowX: "auto",
              overflowY: "hidden",
              borderBottom: "1px solid rgba(0, 82, 165, 0.15)",
              backgroundColor: "#ffffff"
            }}
          >
            {items.map((item) => {
              const hasChildren = menuData.some(
                x => x.parentMenuId === item.menuId && x.isActive
              );
              const isSelected = selectedPath.includes(item.menuId);
              const hasContent = item.hasContent !== false && item.hasContent !== 0;

              return (
                <a
                  key={item.menuId}
                  href={getMenuHref(item.menuId)}
                  onClick={(e) => {
                    // Let the browser handle modified/middle clicks via the
                    // real href ("open in new tab", ctrl/⌘+click, etc.).
                    if (isModifiedClick(e)) return;
                    e.preventDefault();
                    if (isMobile()) {
                      const zone = calcZone(e.currentTarget);
                      setParentZones(prev => ({ ...prev, [item.menuId]: zone }));
                      handlePathUpdate(level, item.menuId);
                      if (hasChildren) {
                        setMobileMenuLevel(level + 1);
                      }
                    }
                    if (hasContent || item.hasDynamicForm || item.hasSearchPage || item.routePath || pdfMenuMap[Number(item.menuId)]) {
                      handleNavigate(item.menuId, hasChildren);
                    }
                  }}
                  onMouseEnter={(e) => {
                    if (!isMobile() && hasChildren) {
                      const zone = calcZone(e.currentTarget);
                      setParentZones(prev => ({ ...prev, [item.menuId]: zone }));
                      handlePathUpdate(level, item.menuId);
                    }
                    if (!selectedPath.includes(item.menuId)) {
                      e.target.style.backgroundColor = "rgba(0, 82, 165, 0.18)";
                      e.target.style.color = "#0052A5";
                    }
                  }}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "4px 12px",
                    margin: "2px 6px",
                    cursor: "pointer",
                    borderRadius: "4px",
                    backgroundColor: isSelected ? "#0052A5" : "transparent",
                    color: isSelected ? "#ffffff" : "#222222",
                    fontWeight: isSelected ? "600" : "normal",
                    fontSize: "14px",
                    transition: "all 0.2s ease",
                    whiteSpace: "nowrap",
                    border: isSelected ? "none" : "1px solid transparent"
                  }}
                  onMouseLeave={(e) => {
                    if (!selectedPath.includes(item.menuId)) {
                      e.target.style.backgroundColor = "transparent";
                      e.target.style.color = "#222222";
                    }
                  }}
                >
                  {item.menuTitle}
                  {hasChildren && (
                    <span style={{ marginLeft: "6px", fontSize: "10px" }}>&#9660;</span>
                  )}
                </a>
              );
            })}
          </div>
        );
      }
    }

    return levels.length > 0 ? (
      <div ref={menuRef} className="floating-submenu">
        {levels}
      </div>
    ) : null;
  };

  // =========================
  // DESKTOP LEFT-SIDE SUBMENU PANEL
  // =========================
  // A left column IN the page flow (the routed content reflows beside it) —
  // replaces the old horizontal strip under the navbar. It ALWAYS shows a
  // submenu: Products when logged out, Sales when logged in, or whichever
  // top-level container (Products, Sales) was clicked last. Deeper levels
  // expand in place (accordion), since a third level can't be another
  // horizontal row. panelPath (selectedPath, or the default root when
  // nothing is selected) doubles as the expanded-branch path:
  // [topId, level1Id, level2Id, …].
  //
  // Default root follows the CURRENT PAGE first (a Products page shows the
  // Products submenu even when logged in); when the route has no submenu
  // owner (/home, /dashboard, …) it falls back to Sales / Products by login.
  const routeRootHasChildren = activeTopMenuId != null && menuData.some(
    m => m.parentMenuId === activeTopMenuId && m.isActive && canAccessMenu(m)
  );
  const DEFAULT_PANEL_ROOT = routeRootHasChildren
    ? activeTopMenuId
    : (isLoggedIn ? 2063 : 2); // Sales : Products
  const panelPath = selectedPath.length > 0 ? selectedPath : [DEFAULT_PANEL_ROOT];

  const renderSidePanelItems = (parentId, level) => {
    const items = menuData.filter(
      x => x.parentMenuId === parentId && x.isActive && canAccessMenu(x)
    );
    if (items.length === 0) return null;

    return (
      <ul className={level > 1 ? "side-panel-list side-panel-nested" : "side-panel-list"}>
        {items.map(item => {
          const hasChildren = menuData.some(
            x => x.parentMenuId === item.menuId && x.isActive && canAccessMenu(x)
          );
          const isExpanded = panelPath[level] === item.menuId;
          const ItemIcon = panelIconFor(item);
          return (
            <li key={item.menuId}>
              <a
                href={getMenuHref(item.menuId)}
                className={"side-panel-item" + (isExpanded ? " expanded" : "")}
                onClick={(e) => {
                  // Modified/middle clicks fall through to the browser so the
                  // href opens in a new tab/window.
                  if (isModifiedClick(e)) return;
                  e.preventDefault();
                  if (hasChildren) {
                    setSelectedPath(isExpanded
                      ? panelPath.slice(0, level)
                      : [...panelPath.slice(0, level), item.menuId]);
                    if (!isExpanded && isNavigable(item)) {
                      handleNavigate(item.menuId, true);
                    }
                  } else if (isNavigable(item)) {
                    // Leaf: navigate and close the floating drawer.
                    handleNavigate(item.menuId);
                    setSelectedPath([]);
                    setPanelOpen(false);
                  }
                }}
              >
                <span className="side-panel-icon" aria-hidden="true"><ItemIcon /></span>
                <span className="side-panel-label">{item.menuTitle}</span>
                {hasChildren && (
                  <span className={"side-panel-caret" + (isExpanded ? " open" : "")}>&#9656;</span>
                )}
              </a>
              {isExpanded && renderSidePanelItems(item.menuId, level + 1)}
            </li>
          );
        })}
      </ul>
    );
  };

  const renderSidePanel = () => {
    // Docked mode (logged-in app pages): always rendered, content reflows
    // beside it. Otherwise a floating drawer rendered only while open —
    // opened by clicking Products/Sales in the top bar; shows the clicked
    // submenu (panelPath falls back to the contextual default should the
    // drawer open without an explicit selection).
    if (isMobile() || (!isDockedMenu && !panelOpen)) return null;

    const rootItem = menuData.find(
      m => m.menuId === panelPath[0] && m.isActive && canAccessMenu(m)
    );
    if (!rootItem) return null;

    return (
      <div ref={menuRef} className={"side-menu-panel" + (isDockedMenu ? " docked" : "")}>
        <div className="side-panel-inner">
          <div className="side-panel-header">
            <span className="side-panel-title">{rootItem.menuTitle}</span>
          </div>
          <div className="side-panel-body">
            {renderSidePanelItems(rootItem.menuId, 1)}
          </div>
        </div>
      </div>
    );
  };

  // =========================
  // RENDER
  // =========================
  return (
    <>
      {/* Clickable overlay to close mobile menu */}
      {isActive && (
        <div
          onClick={() => { setIsActive(false); setSelectedPath([]); setMobileMenuLevel(0); }}
          style={{
            position: "fixed",
            top: 0, left: 0, right: 0, bottom: 0,
            zIndex: 1049,
            background: "transparent"
          }}
        />
      )}

      {/* Floating oval navbar */}
      <div className="floating-nav-wrapper">
        <Navbar expand="lg" expanded={isActive} onToggle={() => toggleMenu()} ref={navbarRef}>
          <Navbar.Brand style={{ cursor: "pointer" }} onClick={() => { handleNavigate(1); setSelectedPath([]); setPanelOpen(false); setIsActive(false); setMobileMenuLevel(0); }}>
            <img
              src="/Images/SideLogoSlogan.webp"
              alt="Logo"
            />
          </Navbar.Brand>

          <Navbar.Toggle onClick={toggleMenu} />
          <Navbar.Collapse>
            {isLoading ? (
              <div style={{ padding: "10px" }}>
                <Spinner animation="border" size="sm" /> Loading menu...
              </div>
            ) : error ? (
              <Alert variant="danger" style={{ margin: "10px", padding: "8px" }}>
                {error}
              </Alert>
            ) : (
              <>
                {/* Mobile back button */}
                {mobileMenuLevel > 0 && isMobile() && (
                  <div
                    onClick={() => {
                      if (mobileMenuLevel === 1) {
                        setMobileMenuLevel(0);
                        setSelectedPath([]);
                      } else {
                        setMobileMenuLevel(mobileMenuLevel - 1);
                        setSelectedPath(prev => prev.slice(0, -1));
                      }
                    }}
                    style={{
                      padding: "10px 18px",
                      cursor: "pointer",
                      color: "#0052A5",
                      fontWeight: 600,
                      fontSize: "14px",
                      borderBottom: "1px solid rgba(0, 82, 165, 0.12)",
                      display: "flex",
                      alignItems: "center",
                      gap: "8px"
                    }}
                  >
                    &larr; Back
                  </div>
                )}
                {(mobileMenuLevel === 0 || !isMobile()) && (
                  <Nav className="w-100">{renderTopMenu()}</Nav>
                )}
                {/* Mobile drill-down: render the submenu INSIDE the collapse
                    panel so the chosen level's items (e.g. Sales → Territory /
                    Sales Rep) flow within the open menu. On desktop the submenu
                    instead floats below the pill (rendered after </Navbar>). */}
                {isMobile() && mobileMenuLevel > 0 && renderSubMenus()}
              </>
            )}
          </Navbar.Collapse>

          {/* Right-side icons */}
          <div className="navbar-right-icons">
            {/* Role Access matrix — IT only: admin-equivalent role on
                the D99 root territory (or no territory claim). Mirrors
                RoleAccessController.IsShuklaIt, which re-checks server-side. */}
            {isLoggedIn && (() => {
              const role = (sessionStorage.getItem("userRole") || "").toLowerCase();
              const terr = (sessionStorage.getItem("territoryId") || "").toUpperCase();
              const isShuklaIt = ["shukla", "it", "admin", "testers", "usahedrep"].includes(role)
                && (terr === "D99" || terr === "");
              return isShuklaIt ? (
                <button
                  className="navbar-icon-btn"
                  title="Role Access"
                  aria-label="Role Access"
                  onClick={() => navigate("/role-access")}
                >
                  <FaUserShield />
                </button>
              ) : null;
            })()}
            {/* Dashboard icon follows the Dashboard menu's entitlement
                (Menu 2079 → RequiredActionId, "00054"). */}
            {isLoggedIn && hasAction(
              menuData.find((m) => Number(m.menuId) === 2079)?.requiredActionId || "00054", true) && (
              <button
                className="navbar-icon-btn"
                title="Sales Dashboard"
                aria-label="Sales Dashboard"
                onClick={() => {
                  // Clicking the dashboard icon should always land the user
                  // at the top of the dashboard, even if they're already on
                  // the page (deep inside a drill-down or with stale filter
                  // state). react-router treats `navigate(samePath)` as a
                  // no-op, so when we're already on a /dashboard* route we
                  // do a full page reload instead — same effect the user
                  // would get from re-typing the URL.
                  if (location.pathname === "/dashboard" || location.pathname.startsWith("/dashboard/")) {
                    window.location.href = "/dashboard";
                  } else {
                    navigate("/dashboard");
                  }
                }}
              >
                <FaChartLine />
              </button>
            )}
            <button
              className="navbar-icon-btn"
              title="Contact Us"
              aria-label="Contact Us"
              onClick={() => navigate(`/home/${buildSlugPath(menuData, 64) || '64'}`)}
            >
              <FaPhone />
            </button>
            {isLoggedIn ? (
              <button
                className="navbar-icon-btn"
                title="Logout"
                aria-label="Logout"
                onClick={() => {
                  clearAuth();
                  window.location.href = '/home';
                }}
              >
                <FaSignOutAlt />
              </button>
            ) : (
              <button
                className="navbar-icon-btn"
                title="Login"
                aria-label="Login"
                onClick={() => navigate("/login")}
              >
                <FaUserCircle />
              </button>
            )}
          </div>
        </Navbar>
      </div>

      {/* Desktop floating drawer — overlays the content below the navbar.
          Opened via the navbar menu icon or by clicking Products/Sales; on
          mobile the submenu renders inside the collapse panel (drill-down). */}
      {renderSidePanel()}

      {/* Page content — always full width; the drawer floats above it.
          Child routes receive menuData so slug-aware children (Sidebar,
          DynamicForm, DynamicSearch) can resolve `/home/products/shukla-hip/
          blade` style paths locally without re-fetching the menu tree.
          Logged-in pages share one consistent backdrop (`.app-bg`); public
          marketing pages render their own full-bleed content. */}
      <div className={isLoggedIn
        ? "app-bg" + (isDockedMenu ? " app-bg-docked-menu" : "")
        : undefined}>
        <Outlet context={{ menuData }} />
      </div>
    </>
  );
}

export default NavbarLayout;
